<template>
  <section class="customer-page">
    <div class="page-header">
      <div>
        <h2>客户列表</h2>
        <p>承接企业认证审批、正式客户管理、关联账号维护和客户订单维护。</p>
      </div>
    </div>

    <el-tabs v-model="activeTab" class="customer-tabs">
      <el-tab-pane name="customer">
        <template #label>
          <span>客户列表</span>
        </template>

        <div class="toolbar-card">
          <div class="toolbar-grid">
            <el-input v-model="customerFilters.keyword" placeholder="搜索企业名称 / 营业执照号 / 联系人 / 电话" clearable />
            <el-select v-model="customerFilters.certificationStatus" clearable placeholder="认证状态">
              <el-option label="全部" :value="null" />
              <el-option label="已认证" :value="2" />
              <el-option label="认证失败" :value="3" />
            </el-select>
            <el-select v-model="customerFilters.status" clearable placeholder="客户状态">
              <el-option label="全部" :value="null" />
              <el-option label="启用" :value="1" />
              <el-option label="禁用" :value="2" />
            </el-select>
            <el-date-picker
              v-model="customerFilters.dateRange"
              type="daterange"
              value-format="x"
              range-separator="至"
              start-placeholder="创建开始日期"
              end-placeholder="创建结束日期"
            />
          </div>
          <div class="toolbar-actions">
            <el-button type="primary" @click="searchCustomerList">查询</el-button>
            <el-button @click="resetCustomerFilters">重置</el-button>
            <el-button :loading="customerExporting" @click="exportRows('customer')">导出</el-button>
            <TableColumnSettings table-key="account-enterprise-customer" :columns="customerTableColumns" @update="columns => customerConfiguredColumns = columns" />
            <el-button type="primary" plain @click="openCustomerCreateDialog">新增客户</el-button>
          </div>
        </div>

        <div class="table-card">
          <el-table v-loading="customerLoading" :data="customerList" border>
            <el-table-column
              v-for="column in visibleCustomerColumns"
              :key="column.key"
              :prop="column.prop"
              :label="column.label"
              :width="column.width"
              :min-width="column.minWidth"
              :show-overflow-tooltip="column.showOverflowTooltip"
            >
              <template #default="{ row }">
                <el-tag v-if="column.key === 'certificationStatus'" :type="certificationTagType(row.certificationStatus)">
                  {{ row.certificationStatusDesc || '-' }}
                </el-tag>
                <el-tag v-else-if="column.key === 'status'" :type="row.status === 1 ? 'success' : 'danger'">{{ row.statusDesc || '-' }}</el-tag>
                <span v-else-if="column.key === 'createTime'">{{ formatTime(row.createTime) }}</span>
                <span v-else>{{ row[column.prop] || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" fixed="right" width="220">
              <template #default="{ row }">
                <el-button link type="primary" @click="openCustomerDetail(row.id)">详情</el-button>
                <el-button link :type="row.status === 1 ? 'danger' : 'primary'" @click="toggleCompanyStatus(row)">
                  {{ row.status === 1 ? '禁用' : '启用' }}
                </el-button>
                <el-button link type="danger" :disabled="row.status !== 2" @click="deleteCustomer(row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <Pagination :page="customerPage" :size="customerSize" :total="customerTotal" label="客户记录" @current-change="handleCustomerPageChange" />
      </el-tab-pane>

      <el-tab-pane name="audit">
        <template #label>
          <span>
            认证审批
            <el-badge v-if="pendingAuditCount > 0" :value="pendingAuditCount" class="tab-badge" />
          </span>
        </template>

        <div class="toolbar-card">
          <div class="toolbar-grid">
            <el-input v-model="auditFilters.keyword" placeholder="搜索企业名称 / 营业执照号 / 联系人 / 电话" clearable />
            <el-select v-model="auditFilters.certificationStatus" clearable placeholder="审批状态">
              <el-option label="全部" :value="null" />
              <el-option label="认证中" :value="1" />
              <el-option label="认证失败" :value="3" />
            </el-select>
            <el-date-picker
              v-model="auditFilters.dateRange"
              type="daterange"
              value-format="x"
              range-separator="至"
              start-placeholder="申请开始日期"
              end-placeholder="申请结束日期"
            />
          </div>
          <div class="toolbar-actions">
            <el-button type="primary" @click="searchAuditList">查询</el-button>
            <el-button @click="resetAuditFilters">重置</el-button>
            <el-button :loading="auditExporting" @click="exportRows('audit')">导出</el-button>
          </div>
        </div>

        <div class="table-card">
          <el-table v-loading="auditLoading" :data="auditList" border>
            <el-table-column prop="customerId" label="申请ID" width="110" />
            <el-table-column prop="name" label="企业名称" min-width="220" show-overflow-tooltip />
            <el-table-column prop="licenseNo" label="营业执照号" min-width="180" show-overflow-tooltip />
            <el-table-column prop="contactName" label="联系人" width="120" />
            <el-table-column prop="contactPhone" label="联系电话" width="140" />
            <el-table-column label="申请账号" min-width="220">
              <template #default="{ row }">
                <div class="link-user">
                  <span>{{ row.primaryUsername || '-' }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="审批状态" width="120">
              <template #default="{ row }">
                <el-tag :type="certificationTagType(row.certificationStatus)">{{ row.certificationStatusDesc }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="创建时间" width="170">
              <template #default="{ row }">{{ formatTime(row.createTime) }}</template>
            </el-table-column>
            <el-table-column label="操作" fixed="right" width="150">
              <template #default="{ row }">
                <el-button link type="primary" @click="openAuditDrawer(row.id, row.certificationStatus === 1)">
                  {{ row.certificationStatus === 1 ? '审批' : '查看' }}
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <Pagination :page="auditPage" :size="auditSize" :total="auditTotal" label="审批记录" @current-change="handleAuditPageChange" />
      </el-tab-pane>
    </el-tabs>

    <el-drawer
      v-model="detailDrawer.visible"
      size="960px"
      :close-on-click-modal="false"
      :destroy-on-close="false"
      class="customer-drawer"
    >
      <template #header>
        <div class="drawer-header">
          <div>
            <h3>{{ detailDrawer.mode === 'audit' ? '企业认证审核' : '客户详情' }}</h3>
            <p>{{ detailDrawer.data?.name || '-' }}</p>
          </div>
          <div class="drawer-header-tags">
            <el-tag v-if="detailDrawer.data" :type="certificationTagType(detailDrawer.data.certificationStatus)">
              {{ detailDrawer.data.certificationStatusDesc }}
            </el-tag>
            <el-tag v-if="detailDrawer.data" :type="detailDrawer.data.status === 1 ? 'success' : 'danger'">
              {{ detailDrawer.data.statusDesc }}
            </el-tag>
          </div>
        </div>
      </template>

      <div v-if="detailDrawer.data" v-loading="detailDrawer.loading" class="drawer-body">
        <div class="info-card">
          <div class="section-title">企业基础信息</div>
          <div class="info-grid">
            <div class="info-item">
              <span>客户ID</span>
              <strong>CUS{{ detailDrawer.data.id }}</strong>
            </div>
            <div class="info-item">
              <span>创建时间</span>
              <strong>{{ formatTime(detailDrawer.data.createTime) }}</strong>
            </div>
            <div class="info-item">
              <span>企业名称</span>
              <strong>{{ detailDrawer.data.name || '-' }}</strong>
            </div>
            <div class="info-item">
              <span>营业执照号</span>
              <strong>{{ detailDrawer.data.licenseNo || '-' }}</strong>
            </div>
            <div class="info-item">
              <span>联系人</span>
              <strong>{{ detailDrawer.data.contactName || '-' }}</strong>
            </div>
            <div class="info-item">
              <span>联系电话</span>
              <strong>{{ detailDrawer.data.contactPhone || '-' }}</strong>
            </div>
            <div class="info-item info-item-wide">
              <span>企业地址</span>
              <strong>{{ detailDrawer.data.address || '-' }}</strong>
            </div>
            <div class="info-item info-item-wide">
              <span>营业执照文件</span>
              <el-link
                v-if="detailDrawer.data.businessLicenseUrl"
                :href="detailDrawer.data.businessLicenseUrl"
                target="_blank"
                type="primary"
              >
                查看文件
              </el-link>
              <strong v-else>-</strong>
            </div>
            <div v-if="detailDrawer.data.certificationRemark" class="info-item info-item-wide">
              <span>审核备注</span>
              <strong>{{ detailDrawer.data.certificationRemark }}</strong>
            </div>
          </div>
        </div>

        <div
          v-if="detailDrawer.mode === 'audit' && detailDrawer.canAudit && detailDrawer.data.duplicateCompanies?.length"
          class="duplicate-card"
        >
          <div class="section-title">检测到重复企业，请选择归并目标</div>
          <el-radio-group v-model="auditForm.mergeCompanyId" class="duplicate-options">
            <el-radio
              v-for="item in detailDrawer.data.duplicateCompanies"
              :key="item.id"
              :label="item.id"
              class="duplicate-option"
            >
              <div class="duplicate-option-content">
                <strong>{{ item.name }}</strong>
                <span>营业执照号：{{ item.licenseNo || '-' }}</span>
                <span>联系人：{{ item.contactName || '-' }} / {{ item.contactPhone || '-' }}</span>
                <span>认证状态：{{ item.certificationStatusDesc }}，客户状态：{{ item.statusDesc }}</span>
              </div>
            </el-radio>
          </el-radio-group>
        </div>

        <div v-if="detailDrawer.mode === 'audit' && detailDrawer.canAudit" class="audit-card">
          <div class="section-title">审批操作</div>
          <el-form label-position="top">
            <el-form-item label="拒绝原因">
              <el-input
                v-model="auditForm.certificationRemark"
                type="textarea"
                :rows="4"
                maxlength="200"
                show-word-limit
                placeholder="拒绝时必填，长度 10-200 字"
              />
            </el-form-item>
          </el-form>
        </div>

        <template v-if="detailDrawer.mode === 'detail'">
          <el-tabs v-model="detailTab" class="detail-tabs">
            <el-tab-pane label="关联账号" name="account">
              <div class="account-card">
                <div class="account-header">
                  <div class="section-title">关联账号</div>
                  <div class="account-actions">
                    <el-button type="primary" @click="openAccountDialog()">新增账号</el-button>
                    <el-button @click="openBindDialog()">关联已有账号</el-button>
                  </div>
                </div>

                <el-table :data="detailDrawer.data.users || []" border>
                  <el-table-column prop="username" label="用户名" min-width="140" />
                  <el-table-column prop="email" label="邮箱" min-width="180" show-overflow-tooltip />
                  <el-table-column prop="phone" label="手机号" width="140" />
                  <el-table-column prop="roleName" label="角色" width="120" />
                  <el-table-column label="状态" width="100">
                    <template #default="{ row }">
                      <el-tag :type="row.status === 1 ? 'success' : 'danger'">{{ row.statusName }}</el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column label="创建时间" width="170">
                    <template #default="{ row }">{{ formatTime(row.createTime) }}</template>
                  </el-table-column>
                  <el-table-column label="操作" width="170" fixed="right">
                    <template #default="{ row }">
                      <el-button link type="primary" @click="openAccountDialog(row)">编辑</el-button>
                      <el-button link :type="row.status === 1 ? 'danger' : 'primary'" @click="toggleUserStatus(row)">
                        {{ row.status === 1 ? '禁用' : '启用' }}
                      </el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </el-tab-pane>

            <el-tab-pane label="订单管理" name="order">
              <div class="order-card">
                <div class="account-header">
                  <div class="section-title">客户订单</div>
                  <div class="account-actions">
                    <el-button @click="gotoOrderManage">去订单管理页</el-button>
                    <el-button type="primary" @click="openOrderDialog()">新增订单</el-button>
                  </div>
                </div>

                <div class="order-toolbar">
                  <el-input
                    v-model="orderFilters.keyword"
                    clearable
                    placeholder="搜索订单号 / 提单号 / 船司 / 起运港"
                    @keyup.enter="searchCustomerOrders"
                  />
                  <div class="order-toolbar-actions">
                    <el-button type="primary" @click="searchCustomerOrders">查询</el-button>
                    <el-button @click="resetCustomerOrders">重置</el-button>
                  </div>
                </div>

                <el-table v-loading="orderLoading" :data="orderList" border>
                  <el-table-column label="订单号" min-width="150">
                    <template #default="{ row }">{{ row.orderNo || '-' }}</template>
                  </el-table-column>
                  <el-table-column prop="blNo" label="提单号" min-width="180" show-overflow-tooltip />
                  <el-table-column label="创建人" width="120">
                    <template #default="{ row }">{{ row.createUser || '-' }}</template>
                  </el-table-column>
                  <el-table-column label="创建时间" width="170">
                    <template #default="{ row }">{{ formatTime(row.createTime) }}</template>
                  </el-table-column>
                  <el-table-column label="操作" width="150" fixed="right">
                    <template #default="{ row }">
                      <el-button link type="primary" @click="openOrderDialog(row)">编辑</el-button>
                      <el-button link type="danger" @click="deleteOrder(row)">删除</el-button>
                    </template>
                  </el-table-column>
                </el-table>

                <Pagination :page="orderPage" :size="orderSize" :total="orderTotal" label="订单记录" @current-change="handleOrderPageChange" />
              </div>
            </el-tab-pane>
          </el-tabs>
        </template>

        <div v-else class="account-card">
          <div class="account-header">
            <div class="section-title">关联账号</div>
          </div>

          <el-table :data="detailDrawer.data.users || []" border>
            <el-table-column prop="username" label="用户名" min-width="140" />
            <el-table-column prop="email" label="邮箱" min-width="180" show-overflow-tooltip />
            <el-table-column prop="phone" label="手机号" width="140" />
            <el-table-column prop="roleName" label="角色" width="120" />
            <el-table-column label="状态" width="100">
              <template #default="{ row }">
                <el-tag :type="row.status === 1 ? 'success' : 'danger'">{{ row.statusName }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="创建时间" width="170">
              <template #default="{ row }">{{ formatTime(row.createTime) }}</template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <template #footer>
        <div class="drawer-footer">
          <el-button @click="detailDrawer.visible = false">关闭</el-button>
          <template v-if="detailDrawer.mode === 'audit' && detailDrawer.canAudit">
            <el-button type="danger" @click="rejectAudit">审核拒绝</el-button>
            <el-button type="primary" @click="approveAudit">审核通过</el-button>
          </template>
        </div>
      </template>
    </el-drawer>

    <el-dialog v-model="accountDialog.visible" :title="accountDialog.form.id ? '编辑账号' : '新增账号'" width="560px" destroy-on-close>
      <el-form label-position="top">
        <el-form-item label="名" required>
          <el-input v-model="accountDialog.form.firstName" placeholder="请输入名" />
        </el-form-item>
        <el-form-item label="姓" required>
          <el-input v-model="accountDialog.form.lastName" placeholder="请输入姓" />
        </el-form-item>
        <el-form-item label="邮箱" required>
          <el-input v-model="accountDialog.form.email" placeholder="请输入邮箱" />
        </el-form-item>
        <el-form-item label="手机号">
          <el-input v-model="accountDialog.form.phone" placeholder="请输入手机号" />
        </el-form-item>
        <el-form-item label="密码" :required="!accountDialog.form.id">
          <el-input v-model="accountDialog.form.password" type="password" show-password placeholder="6-20位，含大小写字母和数字" />
        </el-form-item>
        <el-form-item label="角色" required>
          <el-select v-model="accountDialog.form.role" placeholder="请选择角色">
            <el-option v-for="item in customerRoles" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="accountDialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="accountDialog.loading" @click="submitAccount">保存</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="customerDialog.visible" title="新增客户" width="720px" destroy-on-close>
      <el-form label-position="top">
        <div class="dialog-grid">
          <el-form-item label="企业名称" required>
            <el-input v-model="customerDialog.form.name" placeholder="请输入企业名称" />
          </el-form-item>
          <el-form-item label="营业执照号" required>
            <el-input v-model="customerDialog.form.licenseNo" placeholder="请输入营业执照号" />
          </el-form-item>
          <el-form-item label="联系人" required>
            <el-input v-model="customerDialog.form.contactName" placeholder="请输入联系人" />
          </el-form-item>
          <el-form-item label="联系电话" required>
            <el-input v-model="customerDialog.form.contactPhone" placeholder="请输入联系电话" />
          </el-form-item>
          <el-form-item class="dialog-grid-wide" label="企业地址">
            <el-input v-model="customerDialog.form.address" placeholder="请输入企业地址" />
          </el-form-item>
          <el-form-item class="dialog-grid-wide" label="营业执照文件地址">
            <el-input v-model="customerDialog.form.businessLicenseUrl" placeholder="请输入营业执照文件 URL" />
          </el-form-item>
        </div>

        <div class="subsection-title">关联主账号</div>

        <div class="dialog-grid">
          <el-form-item label="名" required>
            <el-input v-model="customerDialog.form.firstName" placeholder="请输入名" />
          </el-form-item>
          <el-form-item label="姓" required>
            <el-input v-model="customerDialog.form.lastName" placeholder="请输入姓" />
          </el-form-item>
          <el-form-item label="邮箱" required>
            <el-input v-model="customerDialog.form.email" placeholder="请输入邮箱" />
          </el-form-item>
          <el-form-item label="手机号">
            <el-input v-model="customerDialog.form.phone" placeholder="请输入手机号" />
          </el-form-item>
          <el-form-item label="角色" required>
            <el-select v-model="customerDialog.form.role" placeholder="请选择角色">
              <el-option v-for="item in customerRoles" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </el-form-item>
          <el-form-item class="dialog-grid-wide" label="登录密码" required>
            <el-input v-model="customerDialog.form.password" type="password" show-password placeholder="6-20位，含大小写字母和数字" />
          </el-form-item>
        </div>
      </el-form>
      <template #footer>
        <el-button @click="customerDialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="customerDialog.loading" @click="submitCustomerCreate">保存</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="bindDialog.visible" title="关联已有账号" width="860px" destroy-on-close>
      <div class="bind-search">
        <el-input v-model="bindDialog.keyword" placeholder="搜索姓名 / 邮箱 / 手机号" clearable @keyup.enter="fetchBindableUsers" />
        <el-button type="primary" :loading="bindDialog.loading" @click="fetchBindableUsers">查询</el-button>
      </div>

      <el-table :data="bindDialog.options" border highlight-current-row @current-change="handleBindCurrentChange" height="320">
        <el-table-column width="60">
          <template #default="{ row }">
            <el-radio v-model="bindDialog.selectedUserId" :label="row.id" />
          </template>
        </el-table-column>
        <el-table-column prop="username" label="姓名" min-width="140" />
        <el-table-column prop="email" label="邮箱" min-width="180" show-overflow-tooltip />
        <el-table-column prop="phone" label="手机号" width="140" />
        <el-table-column prop="companyName" label="当前所属企业" min-width="180" show-overflow-tooltip />
        <el-table-column prop="roleName" label="当前角色" width="120" />
        <el-table-column label="账号状态" width="100">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'">{{ row.statusName }}</el-tag>
          </template>
        </el-table-column>
      </el-table>

      <el-form label-position="top" class="bind-role-form">
        <el-form-item label="绑定后角色" required>
          <el-select v-model="bindDialog.role" placeholder="请选择角色">
            <el-option v-for="item in customerRoles" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="bindDialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="bindDialog.submitting" @click="submitBindUser">确认绑定</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="orderDialog.visible" :title="orderDialog.form.id ? '编辑订单' : '新增订单'" width="620px" destroy-on-close>
      <el-form label-position="top">
        <el-form-item label="订单号">
          <el-input v-model="orderDialog.form.orderNo" placeholder="请输入订单号，可为空" />
        </el-form-item>
        <el-form-item label="提单号" required>
          <el-input v-model="orderDialog.form.blNo" placeholder="请输入提单号" />
        </el-form-item>
        <el-form-item label="承运人">
          <el-select
            v-model="orderDialog.form.carrierCode"
            clearable
            filterable
            placeholder="请选择承运人"
          >
            <el-option v-for="item in carrierOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="起运港" required>
          <el-select
            v-model="orderDialog.form.polCode"
            filterable
            remote
            reserve-keyword
            clearable
            placeholder="请输入港口中文 / 英文 / 代码搜索"
            :remote-method="searchPortOptions"
            :loading="portOptionsLoading"
          >
            <el-option
              v-for="item in portOptions"
              :key="item.portCode"
              :label="formatPortOption(item)"
              :value="item.portCode"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="orderDialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="orderDialog.loading" @click="submitOrder">保存</el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import * as Request from '@/assets/request'
import Pagination from '@/components/spagination'
import TableColumnSettings from '@/components/tableColumnSettings'
import { getVisibleColumns } from '@/utils/tableColumns'

const router = useRouter()
const activeTab = ref('customer')
const detailTab = ref('account')

const customerRoles = [
  { label: '普通用户', value: 30 },
  { label: '客户管理员', value: 70 },
]

const PASSWORD_REG = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,20}$/
const PASSWORD_ERROR_TEXT = '密码长度6-20位，需包含大小写字母和数字'
const validatePassword = (password) => PASSWORD_REG.test(password || '')

const customerFilters = reactive({
  keyword: '',
  certificationStatus: null,
  status: null,
  dateRange: [],
})

const auditFilters = reactive({
  keyword: '',
  certificationStatus: null,
  dateRange: [],
})

const orderFilters = reactive({
  keyword: '',
})

const customerList = ref([])
const customerLoading = ref(false)
const customerExporting = ref(false)
const customerPage = ref(1)
const customerSize = ref(10)
const customerTotal = ref(0)
const customerTableColumns = [
  { key: 'customerId', prop: 'customerId', label: '客户ID', width: 110 },
  { key: 'name', prop: 'name', label: '企业名称', minWidth: 220, showOverflowTooltip: true },
  { key: 'licenseNo', prop: 'licenseNo', label: '营业执照号', minWidth: 180, showOverflowTooltip: true },
  { key: 'contactName', prop: 'contactName', label: '联系人', width: 120 },
  { key: 'contactPhone', prop: 'contactPhone', label: '联系电话', width: 140 },
  { key: 'address', prop: 'address', label: '企业地址', minWidth: 220, showOverflowTooltip: true },
  { key: 'certificationStatus', label: '认证状态', width: 110 },
  { key: 'status', label: '客户状态', width: 100 },
  { key: 'primaryUsername', prop: 'primaryUsername', label: '关联用户名', minWidth: 140, showOverflowTooltip: true },
  { key: 'primaryEmail', prop: 'primaryEmail', label: '关联邮箱', minWidth: 200, showOverflowTooltip: true },
  { key: 'primaryPhone', prop: 'primaryPhone', label: '关联手机号', minWidth: 140, showOverflowTooltip: true },
  { key: 'createTime', label: '创建时间', width: 170 },
]
const customerConfiguredColumns = ref([])
const visibleCustomerColumns = computed(() => getVisibleColumns(customerConfiguredColumns.value))

const auditList = ref([])
const auditLoading = ref(false)
const auditExporting = ref(false)
const auditPage = ref(1)
const auditSize = ref(10)
const auditTotal = ref(0)
const pendingAuditCount = ref(0)

const orderList = ref([])
const orderLoading = ref(false)
const orderPage = ref(1)
const orderSize = ref(10)
const orderTotal = ref(0)

const portOptions = ref([])
const portOptionsLoading = ref(false)
const carrierOptions = ref([])

const detailDrawer = reactive({
  visible: false,
  loading: false,
  mode: 'detail',
  canAudit: false,
  data: null,
})

const auditForm = reactive({
  certificationRemark: '',
  mergeCompanyId: null,
})

const accountDialog = reactive({
  visible: false,
  loading: false,
  form: {
    id: null,
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    role: 30,
  },
})

const customerDialog = reactive({
  visible: false,
  loading: false,
  form: {
    name: '',
    licenseNo: '',
    address: '',
    contactName: '',
    contactPhone: '',
    businessLicenseUrl: '',
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    role: 70,
  },
})

const bindDialog = reactive({
  visible: false,
  loading: false,
  submitting: false,
  keyword: '',
  options: [],
  selectedUserId: null,
  role: 30,
})

const orderDialog = reactive({
  visible: false,
  loading: false,
  form: {
    id: null,
    orderNo: '',
    blNo: '',
    carrierCode: '',
    polCode: '',
  },
})

const currentCompanyId = computed(() => detailDrawer.data?.id || null)

const buildQueryPayload = (filters, page, size) => {
  const [startCreateTime, endCreateTime] = filters.dateRange || []
  return {
    keyword: filters.keyword?.trim() || undefined,
    certificationStatus: filters.certificationStatus ?? undefined,
    status: filters.status ?? undefined,
    startCreateTime: startCreateTime ? Number(startCreateTime) : undefined,
    endCreateTime: endCreateTime ? Number(endCreateTime) : undefined,
    page,
    size,
  }
}

const formatTime = (time) => {
  if (!time) {
    return '-'
  }
  const date = new Date(Number(time))
  if (Number.isNaN(date.getTime())) {
    return '-'
  }
  const pad = (value) => `${value}`.padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const parseFileName = (response, fallback) => {
  const header = response?.headers?.['content-disposition'] || ''
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1])
  }
  const normalMatch = header.match(/filename="?([^"]+)"?/i)
  if (normalMatch?.[1]) {
    return decodeURIComponent(normalMatch[1])
  }
  return fallback
}

const certificationTagType = (status) => {
  if (status === 1) return 'warning'
  if (status === 2) return 'success'
  if (status === 3) return 'danger'
  return 'info'
}

const formatPortOption = (item) => {
  if (!item) {
    return '-'
  }
  return [item.portCnName, item.portEnName, item.portCode].filter(Boolean).join(' / ')
}

const splitUsername = (username = '') => {
  const names = `${username || ''}`.trim().split(/\s+/, 2)
  return {
    firstName: names[0] || '',
    lastName: names[1] || '',
  }
}

const buildDisplayName = (firstName, lastName, fallback = '') => {
  return [firstName, lastName].map((item) => `${item || ''}`.trim()).filter(Boolean).join(' ') || `${fallback || ''}`.trim()
}

const fetchCustomerList = async () => {
  customerLoading.value = true
  const result = await Request.post({
    url: '/company/customer/query',
    data: buildQueryPayload(customerFilters, customerPage.value, customerSize.value),
  })
  customerLoading.value = false
  if (result?.status === 200) {
    customerList.value = result.data.list || []
    customerTotal.value = result.data.total || 0
  }
}

const fetchAuditList = async () => {
  auditLoading.value = true
  const result = await Request.post({
    url: '/company/customer/query',
    data: buildQueryPayload(auditFilters, auditPage.value, auditSize.value),
  })
  auditLoading.value = false
  if (result?.status === 200) {
    auditList.value = result.data.list || []
    auditTotal.value = result.data.total || 0
    if (auditFilters.certificationStatus === 1) {
      pendingAuditCount.value = result.data.total || 0
    }
  }
}

const fetchCustomerOrders = async (options = {}) => {
  if (!currentCompanyId.value) {
    orderList.value = []
    orderTotal.value = 0
    return
  }
  orderLoading.value = !options.hideLoading
  const result = await Request.post({
    url: '/company/customer/order/query',
    data: {
      companyId: currentCompanyId.value,
      keyword: orderFilters.keyword || undefined,
      page: orderPage.value,
      size: orderSize.value,
    },
  })
  orderLoading.value = false
  if (result?.status === 200) {
    orderList.value = result.data.list || []
    orderTotal.value = result.data.total || 0
  }
}

const refreshLists = async () => {
  await Promise.all([fetchCustomerList(), fetchAuditList(), refreshPendingAuditCount()])
}

const refreshPendingAuditCount = async () => {
  const result = await Request.post({
    url: '/company/customer/query',
    data: {
      certificationStatus: 1,
      page: 1,
      size: 1,
    },
  })
  if (result?.status === 200) {
    pendingAuditCount.value = result.data.total || 0
  }
}

const searchCustomerList = () => {
  customerPage.value = 1
  fetchCustomerList()
}

const searchAuditList = () => {
  auditPage.value = 1
  fetchAuditList()
}

const searchCustomerOrders = () => {
  orderPage.value = 1
  fetchCustomerOrders()
}

const resetCustomerFilters = () => {
  customerFilters.keyword = ''
  customerFilters.certificationStatus = null
  customerFilters.status = null
  customerFilters.dateRange = []
  searchCustomerList()
}

const resetAuditFilters = () => {
  auditFilters.keyword = ''
  auditFilters.certificationStatus = null
  auditFilters.dateRange = []
  searchAuditList()
}

const resetCustomerOrders = () => {
  orderFilters.keyword = ''
  searchCustomerOrders()
}

const handleCustomerPageChange = (page) => {
  customerPage.value = page
  fetchCustomerList()
}

const handleAuditPageChange = (page) => {
  auditPage.value = page
  fetchAuditList()
}

const handleOrderPageChange = (page) => {
  orderPage.value = page
  fetchCustomerOrders()
}

const resetDetailState = () => {
  detailTab.value = 'account'
  orderFilters.keyword = ''
  orderPage.value = 1
  orderList.value = []
  orderTotal.value = 0
}

const openCustomerDetail = async (companyId) => {
  await openDetailDrawer(companyId, 'detail', false)
}

const openAuditDrawer = async (companyId, canAudit) => {
  await openDetailDrawer(companyId, 'audit', canAudit)
}

const openDetailDrawer = async (companyId, mode, canAudit) => {
  detailDrawer.visible = true
  detailDrawer.loading = true
  detailDrawer.mode = mode
  detailDrawer.canAudit = canAudit
  detailDrawer.data = null
  auditForm.certificationRemark = ''
  auditForm.mergeCompanyId = null
  resetDetailState()

  const result = await Request.get({
    url: `/company/customer/detail/${companyId}`,
  })
  detailDrawer.loading = false
  if (result?.status === 200) {
    detailDrawer.data = result.data
    if (mode === 'detail') {
      fetchCustomerOrders({ hideLoading: true })
    }
  }
}

const toggleCompanyStatus = async (row) => {
  const target = row.status === 1 ? 'disable' : 'enable'
  const actionText = row.status === 1 ? '禁用' : '启用'
  try {
    await ElMessageBox.confirm(`确定${actionText}该客户及其关联账号吗？`, `${actionText}确认`, {
      type: 'warning',
    })
  } catch (error) {
    return
  }
  const result = await Request.post({
    url: `/company/${target}`,
    data: [row.id],
  })
  if (result?.status === 200) {
    ElMessage.success(`客户已${actionText}`)
    refreshLists()
    if (detailDrawer.visible && currentCompanyId.value === row.id) {
      openCustomerDetail(row.id)
    }
  }
}

const deleteCustomer = async (row) => {
  if (row.status !== 2) {
    ElMessage.warning('请先禁用客户后再删除')
    return
  }
  const userCount = Number(row.userCount) || 0
  const userText = userCount > 0 ? `，并一并删除 ${userCount} 个关联账号` : ''
  try {
    await ElMessageBox.confirm(`确定删除客户“${row.name || row.customerId || row.id}”吗？${userText}。删除后不可在列表中恢复。`, '删除确认', {
      type: 'warning',
      confirmButtonText: '删除',
      cancelButtonText: '取消',
    })
  } catch (error) {
    return
  }
  const result = await Request.post({
    url: `/company/customer/delete/${row.id}`,
  })
  if (result?.status === 200) {
    ElMessage.success('客户已删除')
    if (detailDrawer.visible && currentCompanyId.value === row.id) {
      detailDrawer.visible = false
      currentCompanyId.value = null
    }
    refreshLists()
  }
}

const validateRejectReason = () => {
  const remark = (auditForm.certificationRemark || '').trim()
  if (remark.length < 10 || remark.length > 200) {
    ElMessage.error('请填写 10-200 字的拒绝原因')
    return false
  }
  return true
}

const approveAudit = async () => {
  if (!detailDrawer.data?.id) {
    return
  }
  if (detailDrawer.data.duplicateCompanies?.length && !auditForm.mergeCompanyId) {
    ElMessage.error('存在重复企业，请先选择归并目标')
    return
  }
  try {
    await ElMessageBox.confirm('确认审核通过该企业认证吗？', '审核通过', { type: 'warning' })
  } catch (error) {
    return
  }
  const result = await Request.post({
    url: '/company/customer/audit',
    data: {
      companyId: detailDrawer.data.id,
      certificationStatus: 2,
      mergeCompanyId: auditForm.mergeCompanyId || undefined,
    },
  })
  if (result?.status === 200) {
    ElMessage.success(auditForm.mergeCompanyId ? '认证已通过并归并到目标企业' : '企业认证审核通过')
    detailDrawer.visible = false
    refreshLists()
  }
}

const rejectAudit = async () => {
  if (!detailDrawer.data?.id || !validateRejectReason()) {
    return
  }
  try {
    await ElMessageBox.confirm('确认拒绝该企业认证吗？', '审核拒绝', { type: 'warning' })
  } catch (error) {
    return
  }
  const result = await Request.post({
    url: '/company/customer/audit',
    data: {
      companyId: detailDrawer.data.id,
      certificationStatus: 3,
      certificationRemark: auditForm.certificationRemark.trim(),
    },
  })
  if (result?.status === 200) {
    ElMessage.success('企业认证已驳回')
    detailDrawer.visible = false
    refreshLists()
  }
}

const openAccountDialog = (row) => {
  accountDialog.visible = true
  accountDialog.form = {
    id: row?.id || null,
    firstName: row?.firstName || splitUsername(row?.username).firstName,
    lastName: row?.lastName || splitUsername(row?.username).lastName,
    username: row?.username || '',
    email: row?.email || '',
    phone: row?.phone || '',
    password: '',
    role: row?.role || 30,
  }
}

const resetCustomerCreateForm = () => {
  customerDialog.form = {
    name: '',
    licenseNo: '',
    address: '',
    contactName: '',
    contactPhone: '',
    businessLicenseUrl: '',
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    role: 70,
  }
}

const openCustomerCreateDialog = () => {
  resetCustomerCreateForm()
  customerDialog.visible = true
}

const submitCustomerCreate = async () => {
  const firstName = customerDialog.form.firstName?.trim()
  const lastName = customerDialog.form.lastName?.trim()
  const payload = {
    name: customerDialog.form.name?.trim(),
    licenseNo: customerDialog.form.licenseNo?.trim(),
    address: customerDialog.form.address?.trim() || undefined,
    contactName: customerDialog.form.contactName?.trim(),
    contactPhone: customerDialog.form.contactPhone?.trim(),
    businessLicenseUrl: customerDialog.form.businessLicenseUrl?.trim() || undefined,
    firstName,
    lastName,
    username: buildDisplayName(firstName, lastName, customerDialog.form.username),
    email: customerDialog.form.email?.trim(),
    phone: customerDialog.form.phone?.trim() || undefined,
    password: customerDialog.form.password,
    role: customerDialog.form.role,
  }
  if (!payload.name || !payload.licenseNo || !payload.contactName || !payload.contactPhone || !payload.firstName || !payload.lastName || !payload.username || !payload.email || !payload.password || !payload.role) {
    ElMessage.error('请完整填写客户和主账号信息')
    return
  }
  if (!validatePassword(payload.password)) {
    ElMessage.error(PASSWORD_ERROR_TEXT)
    return
  }
  customerDialog.loading = true
  const result = await Request.post({
    url: '/company/customer/add',
    data: payload,
  })
  customerDialog.loading = false
  if (result?.status === 200) {
    ElMessage.success('客户新增成功，关联账号已自动创建')
    customerDialog.visible = false
    refreshLists()
  }
}

const submitAccount = async () => {
  const { id, email, phone, password, role } = accountDialog.form
  const firstName = accountDialog.form.firstName?.trim()
  const lastName = accountDialog.form.lastName?.trim()
  const username = buildDisplayName(firstName, lastName, accountDialog.form.username)
  if (!firstName || !lastName || !username || !email || !role || (!id && !password)) {
    ElMessage.error('请完整填写账号信息')
    return
  }
  if ((!id || password) && !validatePassword(password)) {
    ElMessage.error(PASSWORD_ERROR_TEXT)
    return
  }
  accountDialog.loading = true
  const result = await Request.post({
    url: id ? '/user/update' : '/user/add',
    data: {
      ...(id ? { id } : {}),
      firstName,
      lastName,
      username,
      email,
      phone,
      password: password || undefined,
      role,
      companyId: currentCompanyId.value,
    },
  })
  accountDialog.loading = false
  if (result?.status === 200) {
    ElMessage.success('账号保存成功')
    accountDialog.visible = false
    openDetailDrawer(currentCompanyId.value, detailDrawer.mode, detailDrawer.canAudit)
    refreshLists()
  }
}

const toggleUserStatus = async (row) => {
  const target = row.status === 1 ? 'disable' : 'enable'
  const actionText = row.status === 1 ? '禁用' : '启用'
  try {
    await ElMessageBox.confirm(`确定${actionText}该账号吗？`, `${actionText}确认`, { type: 'warning' })
  } catch (error) {
    return
  }
  const result = await Request.post({
    url: `/user/${target}`,
    data: [row.id],
  })
  if (result?.status === 200) {
    ElMessage.success(`账号已${actionText}`)
    openDetailDrawer(currentCompanyId.value, detailDrawer.mode, detailDrawer.canAudit)
    refreshLists()
  }
}

const openBindDialog = () => {
  bindDialog.visible = true
  bindDialog.keyword = ''
  bindDialog.options = []
  bindDialog.selectedUserId = null
  bindDialog.role = 30
  fetchBindableUsers()
}

const fetchBindableUsers = async () => {
  bindDialog.loading = true
  const result = await Request.post({
    url: '/company/customer/userOptions',
    data: {
      companyId: currentCompanyId.value,
      keyword: bindDialog.keyword || undefined,
    },
  })
  bindDialog.loading = false
  if (result?.status === 200) {
    bindDialog.options = result.data || []
  }
}

const handleBindCurrentChange = (row) => {
  bindDialog.selectedUserId = row?.id || null
}

const submitBindUser = async () => {
  if (!bindDialog.selectedUserId || !bindDialog.role) {
    ElMessage.error('请选择账号和绑定角色')
    return
  }
  bindDialog.submitting = true
  const result = await Request.post({
    url: '/company/customer/bindUser',
    data: {
      companyId: currentCompanyId.value,
      userId: bindDialog.selectedUserId,
      role: bindDialog.role,
    },
  })
  bindDialog.submitting = false
  if (result?.status === 200) {
    ElMessage.success('账号绑定成功')
    bindDialog.visible = false
    openDetailDrawer(currentCompanyId.value, detailDrawer.mode, detailDrawer.canAudit)
    refreshLists()
  }
}

const searchPortOptions = async (keyword = '') => {
  portOptionsLoading.value = true
  const result = await Request.post({
    url: '/port/query',
    data: {
      portName: keyword || undefined,
      page: 1,
      size: 20,
    },
  })
  portOptionsLoading.value = false
  if (result?.status === 200) {
    portOptions.value = result.data.list || []
  }
}

const fetchCarrierOptions = async () => {
  const result = await Request.post({
    url: '/company/order/carrierOptions',
    data: {},
  })
  if (result?.status === 200) {
    carrierOptions.value = result.data || []
  }
}

const ensurePortOption = (order = {}) => {
  if (!order.polCode) {
    return
  }
  const exists = portOptions.value.some((item) => item.portCode === order.polCode)
  if (!exists) {
    portOptions.value = [
      {
        portCode: order.polCode,
        portCnName: order.polCnName,
        portEnName: order.polEnName,
      },
      ...portOptions.value,
    ]
  }
}

const openOrderDialog = async (row) => {
  orderDialog.visible = true
  orderDialog.form = {
    id: row?.id || null,
    orderNo: row?.orderNo || '',
    blNo: row?.blNo || '',
    carrierCode: row?.carrierCode || '',
    polCode: row?.polCode || '',
  }
  if (row?.polCode) {
    ensurePortOption(row)
  } else {
    await searchPortOptions()
  }
}

const gotoOrderManage = () => {
  if (!currentCompanyId.value) {
    return
  }
  router.push({
    path: '/account/order',
    query: {
      companyId: currentCompanyId.value,
    },
  })
}

const submitOrder = async () => {
  const payload = {
    id: orderDialog.form.id || undefined,
    companyId: currentCompanyId.value,
    orderNo: orderDialog.form.orderNo?.trim() || undefined,
    blNo: orderDialog.form.blNo?.trim(),
    carrierCode: orderDialog.form.carrierCode?.trim() || undefined,
    polCode: orderDialog.form.polCode,
  }
  if (!payload.companyId) {
    ElMessage.error('请先选择客户')
    return
  }
  if (!payload.blNo || !payload.polCode) {
    ElMessage.error('请填写提单号并选择起运港')
    return
  }
  orderDialog.loading = true
  const result = await Request.post({
    url: '/company/customer/order/save',
    data: payload,
  })
  orderDialog.loading = false
  if (result?.status === 200) {
    ElMessage.success('订单保存成功')
    orderDialog.visible = false
    fetchCustomerOrders()
  }
}

const deleteOrder = async (row) => {
  try {
    await ElMessageBox.confirm('删除后前台将无法再通过该订单号或提单号查询物流，是否继续？', '删除订单', {
      type: 'warning',
    })
  } catch (error) {
    return
  }
  const result = await Request.post({
    url: '/company/customer/order/delete',
    data: {
      companyId: currentCompanyId.value,
      orderId: row.id,
    },
  })
  if (result?.status === 200) {
    ElMessage.success('订单已删除')
    fetchCustomerOrders()
  }
}

const exportRows = async (type) => {
  const isAudit = type === 'audit'
  if (isAudit) {
    auditExporting.value = true
  } else {
    customerExporting.value = true
  }
  const response = await Request.download({
    url: '/company/customer/export',
    data: isAudit
      ? buildQueryPayload(auditFilters, auditPage.value, auditSize.value)
      : buildQueryPayload(customerFilters, customerPage.value, customerSize.value),
  })
  if (isAudit) {
    auditExporting.value = false
  } else {
    customerExporting.value = false
  }
  if (!response) {
    return
  }
  const blob = new Blob([response.data], { type: response.data?.type || 'application/octet-stream' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = parseFileName(response, `${isAudit ? '认证审批' : '客户列表'}-${Date.now()}.xlsx`)
  link.click()
  URL.revokeObjectURL(link.href)
  ElMessage.success('客户数据导出完成')
}

watch(activeTab, (value) => {
  if (value === 'customer' && !customerList.value.length) {
    fetchCustomerList()
  }
  if (value === 'audit' && !auditList.value.length) {
    fetchAuditList()
  }
})

watch(detailTab, (value) => {
  if (value === 'order' && detailDrawer.visible && detailDrawer.mode === 'detail' && currentCompanyId.value && !orderList.value.length) {
    fetchCustomerOrders({ hideLoading: true })
  }
})

watch(
  () => detailDrawer.visible,
  (visible) => {
    if (!visible) {
      resetDetailState()
    }
  }
)

onMounted(() => {
  fetchCarrierOptions()
  refreshLists()
})
</script>

<style scoped lang="less">
.customer-page {
  .page-header {
    margin-bottom: 20px;

    h2 {
      margin-bottom: 4px;
      font-size: 20px;
      line-height: 28px;
      color: #1f2937;
    }

    p {
      font-size: 13px;
      color: #6b7280;
    }
  }

  .customer-tabs {
    :deep(.el-tabs__header) {
      margin-bottom: 12px;
    }
    :deep(.el-tabs__item) {
      font-size: 14px;
    }
  }

  .toolbar-card,
  .table-card,
  .info-card,
  .duplicate-card,
  .audit-card,
  .account-card,
  .order-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
  }

  .toolbar-card {
    padding: 12px 16px;
    margin-bottom: 12px;
  }

  .toolbar-grid {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1.5fr;
    gap: 12px;
    align-items: center;
  }

  .toolbar-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
  }

  .table-card {
    padding: 12px;
  }

  .link-user {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .tab-badge {
    margin-left: 8px;
  }

  .drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    h3 {
      margin-bottom: 4px;
      font-size: 22px;
      color: #111827;
    }

    p {
      color: #6b7280;
    }
  }

  .drawer-header-tags {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .drawer-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .section-title {
    margin-bottom: 14px;
    font-size: 16px;
    font-weight: 600;
    color: #111827;
  }

  .info-card,
  .duplicate-card,
  .audit-card,
  .account-card,
  .order-card {
    padding: 16px;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 12px;
    background: #f8fafc;
    border-radius: 8px;

    span {
      font-size: 12px;
      color: #6b7280;
    }

    strong {
      font-size: 13px;
      line-height: 20px;
      color: #111827;
      word-break: break-word;
    }
  }

  .info-item-wide {
    grid-column: 1 / -1;
  }

  .duplicate-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }

  .duplicate-option {
    width: 100%;
    margin-right: 0;
    padding: 14px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #f8fafc;
  }

  .duplicate-option-content {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-left: 8px;
    color: #4b5563;
  }

  .detail-tabs {
    :deep(.el-tabs__nav-wrap::after) {
      background: #e5e7eb;
    }

    :deep(.el-tabs__item) {
      font-weight: 600;
    }
  }

  .account-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
  }

  .account-actions {
    display: flex;
    gap: 12px;
  }

  .order-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
  }

  .order-toolbar-actions {
    display: flex;
    gap: 12px;
    flex-shrink: 0;
  }

  .bind-search {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
  }

  .bind-role-form {
    margin-top: 16px;
  }

  .drawer-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    width: 100%;
  }

  .dialog-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 12px;
  }

  .dialog-grid-wide {
    grid-column: 1 / -1;
  }

  .subsection-title {
    margin: 4px 0 12px;
    font-size: 14px;
    font-weight: 600;
    color: #111827;
  }

  :deep(.el-form-item__label) {
    font-size: 13px;
    color: #4b5563;
    padding-bottom: 4px;
  }

  :deep(.el-input__inner),
  :deep(.el-input) {
    font-size: 13px;
  }

  @media (max-width: 1200px) {
    .toolbar-grid {
      grid-template-columns: 1fr 1fr;
    }
  }

  @media (max-width: 900px) {
    .info-grid,
    .toolbar-grid,
    .dialog-grid {
      grid-template-columns: 1fr;
    }

    .toolbar-actions,
    .account-header,
    .order-toolbar,
    .bind-search {
      flex-direction: column;
      align-items: stretch;
    }

    .account-actions,
    .order-toolbar-actions {
      width: 100%;
    }
  }
}
</style>
